import { useState, useEffect } from 'react';

interface Aspect {
  name: string;
  weight: number;
}

import { Submission } from 'types/evaluation';

interface Judgment {
  overall_summary?: string;
  aspect_scores?: Array<{ value: number }>;
  documentId?: string;
}

type SaveState = 'initial' | 'saved' | 'changed' | 'completed';

interface EvaluationData {
  submissionId: string;
  scores: Record<string, number | string>;
  isRevision: boolean;
  existingJudgmentId: string | undefined;
}

interface UseEvaluationScoresReturn {
  scores: Record<string, Record<string, number | string>>;
  saveStates: Record<string, SaveState>;
  isSubmitting: Record<string, boolean>;
  handleScoreChange: (
    submissionId: string,
    criteriaKey: string,
    value: string
  ) => void;
  handleSubmissionSave: (submission: Submission) => Promise<void>;
}

const useEvaluationScores = (
  submissions: Submission[],
  existingJudgments: Record<string, Judgment>,
  categoryJudgingData: Record<string, { aspects: Aspect[] }>,
  aspects: Aspect[],
  onEvaluationSubmit: (data: EvaluationData) => Promise<void>
): UseEvaluationScoresReturn => {
  const [scores, setScores] = useState<
    Record<string, Record<string, number | string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState<Record<string, boolean>>({});

  // New state for tracking save button states
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({}); // Track save states per submission
  const [originalScores, setOriginalScores] = useState<
    Record<string, Record<string, number | string>>
  >({}); // Track original scores to detect changes

  // Initialize scores from existing judgments
  useEffect(() => {
    const initialScores: Record<string, Record<string, number | string>> = {};
    const initialOriginalScores: Record<
      string,
      Record<string, number | string>
    > = {};
    const initialSaveStates: Record<string, SaveState> = {};

    submissions.forEach(submission => {
      const submissionId = submission.documentId || submission.id || '';
      const judgment = existingJudgments[submissionId];

      if (judgment && judgment.aspect_scores) {
        const submissionScores: Record<string, number | string> = {};
        judgment.aspect_scores.forEach((aspectScore, index) => {
          submissionScores[`criteria${index + 1}`] = aspectScore.value;
        });
        initialScores[submissionId] = submissionScores;
        initialOriginalScores[submissionId] = { ...submissionScores };

        // Set initial save state based on judgment status
        if (
          judgment.overall_summary === 'Draft Final' ||
          judgment.overall_summary === 'Draft'
        ) {
          initialSaveStates[submissionId] = 'saved'; // Draft Final means it was saved
        } else if (judgment.overall_summary === 'Completed Final') {
          initialSaveStates[submissionId] = 'completed'; // Completed Final evaluation
        } else {
          initialSaveStates[submissionId] = 'initial'; // Not started
        }
      } else {
        // Initialize with empty scores based on real judging data
        const judgingData = categoryJudgingData[submission.category];
        const submissionAspects = judgingData?.aspects || [];
        const submissionScores: Record<string, number | string> = {};
        submissionAspects.forEach((_, index) => {
          submissionScores[`criteria${index + 1}`] = '';
        });
        initialScores[submissionId] = submissionScores;
        initialOriginalScores[submissionId] = { ...submissionScores };
        initialSaveStates[submissionId] = 'initial'; // Not started
      }
    });

    setScores(initialScores);
    setOriginalScores(initialOriginalScores);
    setSaveStates(initialSaveStates);
  }, [submissions, existingJudgments, categoryJudgingData]);

  // Helper function to check if scores have changed from original
  const checkForChanges = (
    submissionId: string,
    currentScores: Record<string, number | string>
  ) => {
    const original = originalScores[submissionId] || {};
    const currentState = saveStates[submissionId];

    // Compare current scores with original scores
    const hasChanges = Object.keys(currentScores).some(key => {
      return currentScores[key] !== original[key];
    });

    // Update save state based on changes
    if (
      hasChanges &&
      (currentState === 'saved' || currentState === 'completed')
    ) {
      setSaveStates(prev => ({
        ...prev,
        [submissionId]: 'changed',
      }));
    } else if (!hasChanges && currentState === 'changed') {
      // If no changes and was in changed state, revert to saved
      setSaveStates(prev => ({
        ...prev,
        [submissionId]: 'saved',
      }));
    }
  };

  // Handle score change
  const handleScoreChange = (
    submissionId: string,
    criteriaKey: string,
    value: string
  ) => {
    const numValue = parseInt(value);
    if (numValue >= 0 && numValue <= 10) {
      setScores(prev => {
        const newScores = {
          ...prev,
          [submissionId]: {
            ...prev[submissionId],
            [criteriaKey]: numValue,
          },
        };

        // Check if scores have changed from original and update save state
        checkForChanges(submissionId, newScores[submissionId]);

        return newScores;
      });
    } else if (value === '') {
      setScores(prev => {
        const newScores = {
          ...prev,
          [submissionId]: {
            ...prev[submissionId],
            [criteriaKey]: '',
          },
        };

        // Check if scores have changed from original and update save state
        checkForChanges(submissionId, newScores[submissionId]);

        return newScores;
      });
    }
  };

  // Handle individual submission save
  const handleSubmissionSave = async (submission: Submission) => {
    const submissionId = submission.documentId || submission.id || '';
    const submissionScores = scores[submissionId] || {};

    // Validate all scores are filled
    const allScoresFilled = aspects.every((_, index) => {
      const score = submissionScores[`criteria${index + 1}`];
      const numScore = Number(score);
      return score !== '' && numScore >= 0 && numScore <= 10;
    });

    if (!allScoresFilled) {
      alert('Please fill in all scores (0-10) before saving.');
      return;
    }

    setIsSubmitting(prev => ({ ...prev, [submissionId]: true }));

    try {
      const evaluationData: EvaluationData = {
        submissionId: submissionId,
        scores: submissionScores,
        isRevision: !!existingJudgments[submissionId],
        existingJudgmentId: existingJudgments[submissionId]?.documentId,
      };

      await onEvaluationSubmit(evaluationData);

      // Update save state to 'saved' and update original scores
      setSaveStates(prev => ({
        ...prev,
        [submissionId]: 'saved',
      }));

      setOriginalScores(prev => ({
        ...prev,
        [submissionId]: { ...submissionScores },
      }));
    } catch (error) {
      console.error('Error submitting evaluation:', error);
    } finally {
      setIsSubmitting(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  return {
    scores,
    saveStates,
    isSubmitting,
    handleScoreChange,
    handleSubmissionSave,
  };
};

export default useEvaluationScores;
